<template>
  <el-dialog
    :visible="visible"
    @close="$emit('cancelled')"
    :title="$t('loyalty.title.redeem_reward')"
    width="600px"
    append-to-body
    custom-class="redemption-modal">
    <div v-if="reward" class="px-6 py-4">
      <!-- Reward Info -->
      <div class="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 mb-6">
        <div class="flex items-start space-x-4">
          <div v-if="reward.image" class="flex-shrink-0">
            <img :src="reward.image" :alt="reward.name" class="w-24 h-24 object-cover rounded-lg">
          </div>
          <div v-else class="flex-shrink-0 w-24 h-24 bg-gradient-to-br from-purple-400 to-pink-500 rounded-lg flex items-center justify-center">
            <i class="mdi mdi-gift text-4xl text-white"></i>
          </div>
          <div class="flex-1">
            <h3 class="text-xl font-bold text-gray-900 mb-2">{{ reward.name }}</h3>
            <p class="text-sm text-gray-600 mb-3">{{ reward.description }}</p>
            <div class="flex items-center space-x-4">
              <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                {{ $t('loyalty.reward_type.' + reward.reward_type) }}
              </span>
              <span class="text-lg font-bold text-orange-600">
                <i class="mdi mdi-star mr-1"></i>
                {{ reward.points_required.toLocaleString() }} {{ $t('loyalty.label.points') }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Confirmation Form -->
      <el-form :model="form" :rules="rules" ref="redemptionForm" label-width="180px" class="redemption-form">
        <el-form-item :label="$t('loyalty.label.reference_type')" prop="reference_type" class="mb-6">
          <el-select
            v-model="form.reference_type"
            :placeholder="$t('loyalty.placeholder.reference_type')"
            clearable
            style="width: 100%">
            <el-option label="Sale" value="App\\Sale"></el-option>
            <el-option label="Manual" value="manual"></el-option>
          </el-select>
        </el-form-item>

        <el-form-item
          v-if="form.reference_type"
          :label="$t('loyalty.label.reference_id')"
          prop="reference_id"
          class="mb-6">
          <el-input-number
            v-model="form.reference_id"
            :min="1"
            :placeholder="$t('loyalty.placeholder.reference_id')"
            class="w-full">
          </el-input-number>
        </el-form-item>

        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div class="flex items-start">
            <i class="mdi mdi-information text-yellow-600 text-xl mr-3 mt-0.5"></i>
            <div>
              <p class="text-sm font-medium text-yellow-800 mb-1">{{ $t('loyalty.message.redemption_warning') }}</p>
              <p class="text-xs text-yellow-700">{{ $t('loyalty.message.redemption_irreversible') }}</p>
            </div>
          </div>
        </div>
      </el-form>
    </div>
    <div slot="footer" class="dialog-footer px-6 py-4 bg-gray-50 flex justify-end space-x-3">
      <button
        @click="$emit('cancelled')"
        class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors">
        {{ $t('button.cancel') }}
      </button>
      <button
        @click="confirmRedemption"
        :disabled="saving"
        class="px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
        <span v-if="saving" class="flex items-center">
          <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ $t('status.loading') }}
        </span>
        <span v-else>{{ $t('loyalty.button.confirm_redemption') }}</span>
      </button>
    </div>
  </el-dialog>
</template>

<script>
import ErrorHandlingMixin from '@/mixins/ErrorHandlingMixin.js'

export default {
  name: 'RedemptionModal',
  mixins: [ErrorHandlingMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    reward: {
      type: Object,
      default: null
    },
    customerId: {
      type: [Number, String],
      required: true
    }
  },
  data () {
    return {
      saving: false,
      form: {
        reference_type: null,
        reference_id: null
      }
    }
  },
  computed: {
    rules () {
      return {
        reference_type: [
          // Optional
        ],
        reference_id: [
          {
            validator: (rule, value, callback) => {
              if (this.form.reference_type && !value) {
                callback(new Error('Reference ID is required when reference type is provided'))
              } else {
                callback()
              }
            },
            trigger: 'blur'
          }
        ]
      }
    }
  },
  watch: {
    visible (newVal) {
      if (newVal) {
        this.form.reference_type = null
        this.form.reference_id = null
        if (this.$refs.redemptionForm) {
          this.$refs.redemptionForm.clearValidate()
        }
      }
    }
  },
  methods: {
    async confirmRedemption () {
      if (!this.reward) return

      this.askQuestion(
        this.$t('loyalty.confirm.redeem_reward', { points: this.reward.points_required, name: this.reward.name }),
        async () => {
          try {
            this.saving = true
            await this.$store.dispatch('loyalty/customer/redeemReward', {
              customerId: this.customerId,
              rewardId: this.reward.id,
              referenceType: this.form.reference_type,
              referenceId: this.form.reference_id
            })
            this.$emit('saved')
          } catch (error) {
            console.error('Error redeeming reward:', error)
            this.globalErrHandle(error)
          } finally {
            this.saving = false
          }
        }
      )
    }
  }
}
</script>

<style scoped>
.redemption-modal ::v-deep .el-dialog__body {
  padding: 0;
}

.redemption-form ::v-deep .el-form-item__label {
  font-weight: 500;
  color: #374151;
}

.redemption-form ::v-deep .el-input__inner {
  border-radius: 0.5rem;
  border-color: #d1d5db;
}

.redemption-form ::v-deep .el-input__inner:focus {
  border-color: #3b82f6;
}
</style>
